import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  parseISO,
  format,
  startOfDay,
  endOfDay,
  isSameDay,
  differenceInCalendarDays,
  differenceInCalendarMonths,
} from 'date-fns'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import DialogModal from 'components/display/DialogModal'
import { HeadingMd } from 'components/display/Typography'
import InvisibleButton from 'components/display/InvisibleButton'
import DateRangeCalendar, {
  DayModifiers,
} from 'components/form/DateRangeCalendar'
import AvailabilityInfo from './AvailabilityInfo'
import CalendarDay from './CalendarDay'
import {
  findAvailability,
  buildFindNextUnavailableDay,
  buildIsDayBlocked,
  buildIsOutsideRange,
} from './dayAvailability'
import useFirstUnavailableDate from './useFirstUnavailableDate'
import DatesInput from './DatesInput'
import { CalendarWrapper } from './styles'
import { FocusedInput, DaysAvailabilities, TooltipConfig } from './types'
import useDiscountsVisible from './useDiscountsVisible'

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
  width: calc(100% - 32px);
  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
  padding-bottom: 16px;
  margin-bottom: 8px;

  ${InvisibleButton} {
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
    text-decoration: underline;
    text-underline-offset: 2px;
    font-weight: 400;
    margin-left: 10px;
  }
`

export interface BookingFormCalendarProps {
  start_date?: string
  end_date?: string
  daysData: DaysAvailabilities
  listing: {
    concurrentBookings: boolean
    listingType: {
      slug: string
    }
  }
  maxStartDate: string
  onMonthsForward?: (arg0: Date) => any
  onMonthsBack?: (arg0: Date) => any
  snoozed?: boolean
}

const BookingFormCalendar = (props: BookingFormCalendarProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    start_date,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    end_date,
    daysData,
    listing,
    maxStartDate,
    onMonthsForward,
    onMonthsBack,
    snoozed,
  } = props
  const { setValue, clearErrors } = useFormContext()

  const { lg: largerThanLg } = useMediaQueryContext()
  const [startDate, setStartDate] = useState<Date>(null)
  const [endDate, setEndDate] = useState<Date>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [minimumNights, setMinimumNights] = useState(1)
  // Remembers which input opened the calendar so onDatesChange can
  // honour the user's intent. rdp's range mode resets to a fresh range
  // when both endpoints are set and the user picks any date — that's
  // wrong when the user clicked the End date input meaning to update
  // just the end. We re-map rdp's reset back into a "set new end"
  // semantic in that case.
  const [pendingIntent, setPendingIntent] = useState<FocusedInput | null>(null)

  // react-day-picker v9 manages range focus internally, so we derive
  // 'which date the user is currently selecting' from the current
  // selection state. The dayAvailability helpers branch on this to allow
  // checkout-only dates and to apply minimum-nights to either endpoint.
  const focusedInput: FocusedInput =
    startDate && !endDate ? 'endDate' : 'startDate'

  useEffect(() => {
    if (start_date) setStartDate(parseISO(start_date))
    if (end_date) setEndDate(parseISO(end_date))
  }, [start_date, end_date])

  const onDatesChange = (
    newStartDate: Date | null,
    newEndDate: Date | null
  ): void => {
    let effectiveStartDate = newStartDate
    let effectiveEndDate = newEndDate

    // If the user clicked the End date input meaning to update just the
    // end, but had a complete range, rdp's resetOnSelect collapses the
    // range to a fresh start at the new date. Re-interpret that as
    // updating the existing end (when the click is after start).
    if (
      pendingIntent === 'endDate' &&
      startDate &&
      endDate &&
      newStartDate &&
      !newEndDate &&
      differenceInCalendarDays(newStartDate, startDate) > 0
    ) {
      effectiveStartDate = startDate
      effectiveEndDate = newStartDate
    }

    // react-day-picker's range mode swaps endpoints when the user picks a
    // date earlier than an existing start (so {from:27} + click 26 becomes
    // {from:26, to:27}). The booking calendar treats picking an earlier
    // date as starting a new range, so unswap before the rest of the
    // selection logic runs.
    if (
      startDate &&
      !endDate &&
      effectiveStartDate &&
      effectiveEndDate &&
      differenceInCalendarDays(effectiveStartDate, startDate) < 0 &&
      isSameDay(effectiveEndDate, startDate)
    ) {
      effectiveEndDate = null
    }

    // Whichever interpretation we landed on, the intent has been
    // consumed — clear it so the next interaction picks fresh.
    setPendingIntent(null)

    const availability =
      effectiveStartDate &&
      findAvailability({ daysData, day: effectiveStartDate })

    if (availability?.minimumNights) {
      setMinimumNights(availability.minimumNights)
    }

    // Use bracket notation to match the registered field names in
    // BookingFormFields (where the HTML `name` attribute must be
    // `booking[start_date]` for the listing form's native POST to Rails).
    // RHF normalises both notations to the same internal path, so the
    // BookingChange flow that reads `formValues.booking.end_date` keeps working.
    //
    // Validate when writing a real date so a previously-submitted form's
    // error clears the moment the user picks a valid value. Skip validation
    // on null writes (partial reset of a new range, end-cleared half-pick)
    // so a mid-edit doesn't fabricate a fresh "missing dates" error —
    // that's deferred to the next submit.
    const writeStartDate = (next: Date | null) => {
      setValue(
        'booking[start_date]',
        next ? format(next, 'yyyy-MM-dd') : null,
        {
          shouldDirty: true,
          shouldValidate: !!next,
        }
      )
    }
    const writeEndDate = (next: Date | null) => {
      setValue('booking[end_date]', next ? format(next, 'yyyy-MM-dd') : null, {
        shouldDirty: true,
        shouldValidate: !!next,
      })
    }

    if (
      !startDate ||
      (effectiveStartDate && isSameDay(effectiveStartDate, startDate))
    ) {
      writeStartDate(effectiveStartDate)
      writeEndDate(effectiveEndDate)
      setStartDate(effectiveStartDate)
      setEndDate(effectiveEndDate)
    } else {
      /**
       * QA: If a user already has a date range selected,
       * when they go to select a new date range,
       * clear the existing selection after the first date is selected
       */
      writeStartDate(effectiveStartDate)
      writeEndDate(null)
      setStartDate(effectiveStartDate)
      setEndDate(null)
    }

    if (
      effectiveEndDate &&
      (!endDate || !isSameDay(effectiveEndDate, endDate))
    ) {
      setModalOpen(false)
    }
  }

  const handleDateClick = useCallback((type: 'start' | 'end') => {
    setPendingIntent(type === 'start' ? 'startDate' : 'endDate')
    setModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setPendingIntent(null)
    setModalOpen(false)
  }, [])

  const handleClearDates = useCallback(() => {
    setStartDate(null)
    setEndDate(null)
    setValue('booking[start_date]', null, { shouldDirty: true })
    setValue('booking[end_date]', null, { shouldDirty: true })
    // Manual clear is an explicit user action — discard any standing dates
    // errors so the UI returns to a clean state until the next submit.
    clearErrors(['booking[start_date]', 'booking[end_date]'])
  }, [setValue, clearErrors])

  const maxStart = useMemo(
    () => (maxStartDate ? endOfDay(parseISO(maxStartDate)) : undefined),
    [maxStartDate]
  )

  const isDayBlocked = useMemo(
    () => buildIsDayBlocked({ focusedInput, startDate, daysData }),
    [focusedInput, startDate, daysData]
  )

  const findNextUnavailableDay = useMemo(
    () => buildFindNextUnavailableDay({ daysData }),
    [daysData]
  )

  const today = useMemo(() => startOfDay(new Date()), [new Date().getDay()])
  const firstUnavailableDate = useFirstUnavailableDate(
    startDate,
    findNextUnavailableDay
  )

  const isOutsideRange = useMemo(
    () =>
      buildIsOutsideRange({
        today,
        focusedInput,
        startDate,
        maxStart,
        daysData,
        firstUnavailableDate,
        findNextUnavailableDay,
      }),
    [
      today,
      focusedInput,
      startDate,
      maxStart,
      daysData,
      firstUnavailableDate,
      findNextUnavailableDay,
    ]
  )

  const numberOfMonths = largerThanLg ? 2 : 1
  const { discountsVisible, updateCurrentMonth } = useDiscountsVisible(
    startDate,
    numberOfMonths,
    daysData
  )

  const startDayData =
    startDate && findAvailability({ day: startDate, daysData })

  const [tooltip, setTooltip] = useState<TooltipConfig | null>(null)
  // Only close the popover if the close event corresponds to the
  // currently-open tooltip — clicking a new blocked day opens a fresh
  // tooltip and the previous popover's outside-click fires concurrently.
  const handleCloseTooltip = useCallback((closedTooltip: TooltipConfig) => {
    setTooltip((lastTooltip) =>
      closedTooltip.referenceElement === lastTooltip?.referenceElement
        ? null
        : lastTooltip
    )
  }, [])

  const handleTooltipOpen = useCallback(
    (referenceElement: HTMLElement, message: string) => {
      setTooltip({ referenceElement, message })
    },
    []
  )

  const availabilityInfo = (
    <AvailabilityInfo
      discountsVisible={discountsVisible}
      activeDiscount={startDayData?.discount}
      minimumNights={startDayData?.minimumNights}
      concurrentBookings={listing.concurrentBookings}
      snoozed={snoozed}
      tooltip={tooltip}
      onCloseTooltip={handleCloseTooltip}
    />
  )

  const renderDay = useCallback(
    (day: Date, modifiers: DayModifiers) => (
      <CalendarDay
        focusedInput={focusedInput}
        day={day}
        modifiers={modifiers}
        daysData={daysData}
        today={today}
        maxStartDate={maxStart}
        startDate={startDate}
        firstUnavailableDate={firstUnavailableDate}
        activeDiscount={startDayData?.discount}
        onTooltipOpen={handleTooltipOpen}
      />
    ),
    [
      focusedInput,
      daysData,
      today,
      maxStart,
      startDate,
      startDayData,
      firstUnavailableDate,
      handleTooltipOpen,
    ]
  )

  // rdp v9 surfaces a single `onMonthChange` callback for both prev/next
  // navigation. The data-window fetchers (`onMonthsForward` / `onMonthsBack`)
  // care about direction, so we compare against the previously visible month
  // tracked in a ref.
  const lastVisibleMonth = useRef<Date>(startDate || new Date())
  const handleMonthChange = useCallback(
    (visibleMonth: Date) => {
      updateCurrentMonth(visibleMonth)
      const previous = lastVisibleMonth.current
      lastVisibleMonth.current = visibleMonth
      if (differenceInCalendarMonths(visibleMonth, previous) > 0) {
        onMonthsForward?.(visibleMonth)
      } else if (differenceInCalendarMonths(visibleMonth, previous) < 0) {
        onMonthsBack?.(visibleMonth)
      }
    },
    [onMonthsForward, onMonthsBack, updateCurrentMonth]
  )

  return (
    <>
      <DatesInput
        onDateClick={handleDateClick}
        startDate={startDate}
        endDate={endDate}
      />

      <DialogModal
        isOpen={modalOpen}
        onRequestClose={handleModalClose}
        smallModal
        autoWidth
        closeButtonColour="-ds-neutral-700"
        rounded
        noPadding
      >
        <CalendarWrapper>
          <CalendarHeader>
            <HeadingMd as="h3" weight="500" noMargin>
              {!startDate && !endDate && 'Select dates'}
              {startDate && `${format(startDate, 'dd MMM')} - `}
              {endDate && format(endDate, 'dd MMM')}
            </HeadingMd>

            {startDate && (
              <InvisibleButton
                type="button"
                aria-label="Clear dates"
                onClick={handleClearDates}
              >
                Clear dates
              </InvisibleButton>
            )}
          </CalendarHeader>

          <DateRangeCalendar
            className="booking-calendar-rdp"
            startDate={startDate}
            endDate={endDate}
            onChange={onDatesChange}
            isDayBlocked={isDayBlocked}
            isOutsideRange={isOutsideRange}
            minimumNights={minimumNights}
            minDate={new Date()}
            onMonthChange={handleMonthChange}
            numberOfMonths={numberOfMonths}
            daySize={largerThanLg ? 56 : 48}
            renderDay={renderDay}
            firstDayOfWeek={1}
            defaultMonth={startDate ?? undefined}
            renderBelow={availabilityInfo}
          />
        </CalendarWrapper>
      </DialogModal>
    </>
  )
}

export default BookingFormCalendar
